const Card = ({ label, value, testid }) => (
  <div className="rounded-2xl border-2 border-line bg-surface p-4">
    <p className="text-sm font-bold text-ink-soft">{label}</p>
    <p className="font-display text-3xl" data-testid={testid}>{value ?? 0}</p>
  </div>
);

export const Bars = ({ data, label }) => {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <figure>
      <div className="flex h-28 items-end gap-[3px]" role="img" aria-label={`${label}: ${data.map((d) => `${d.date} ${d.count}`).join(", ")}`}>
        {data.map((d) => <span key={d.date} className="flex-1 rounded-t bg-brand" style={{ height: `${Math.max(3, (d.count / max) * 100)}%`, opacity: d.count ? 1 : 0.25 }} title={`${d.date}: ${d.count}`} />)}
      </div>
      <figcaption className="mt-2 flex justify-between text-xs text-ink-soft"><span>{data[0]?.date}</span><span>{label}</span><span>{data[data.length - 1]?.date}</span></figcaption>
    </figure>
  );
};

export const CountList = ({ title, rows, empty = "None yet." }) => (
  <div>
    <h3 className="mb-2 font-bold">{title}</h3>
    <ul className="space-y-1 text-sm">
      {rows.length ? rows.map((r) => <li key={r.name} className="flex justify-between gap-3 border-b border-line py-1"><span>{r.name}</span><span className="font-bold">{r.count}</span></li>) : <li className="text-ink-soft">{empty}</li>}
    </ul>
  </div>
);

const Box = ({ title, children }) => <section className="rounded-2xl border-2 border-line bg-surface p-5"><h2 className="mb-4 font-display text-2xl font-medium">{title}</h2>{children}</section>;

const VisitorCards = ({ v, newMessages }) => (
  <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6" data-testid="admin-visitor-cards">
    <Card label="Visitors today" value={v?.unique_today} testid="v-today" />
    <Card label="Visitors this week" value={v?.unique_week} testid="v-week" />
    <Card label="Visitors this month" value={v?.unique_month} testid="v-month" />
    <Card label="Visitors all time" value={v?.unique_all_time} testid="v-all" />
    <Card label="Page views" value={v?.total_views} testid="v-views" />
    {newMessages !== undefined && <Card label="New messages" value={newMessages} testid="m-new-messages" />}
  </div>
);

export const Overview = ({ m, v, newMessages = 0 }) => {
  const d = m?.downloads || {};
  return (
    <div className="space-y-5" data-testid="admin-overview">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7">
        <Card label="Total downloads" value={d.total} testid="m-total" />
        <Card label="Today" value={d.today} testid="m-today" />
        <Card label="This week" value={d.week} testid="m-week" />
        <Card label="This month" value={d.month} testid="m-month" />
        <Card label="Feedback" value={m?.feedback?.total} testid="m-feedback" />
        <Card label="Impact reviews" value={m?.impact?.total} testid="m-impact" />
        <Card label="Workbook version" value={m?.workbook?.version || "—"} testid="m-version" />
      </div>
      <VisitorCards v={v} newMessages={newMessages} />
      <Box title="Downloads, last 30 days"><Bars data={d.daily || []} label="Daily downloads" /></Box>
      <div className="grid gap-5 lg:grid-cols-3">
        <Box title="Latest feedback">
          {m?.feedback?.latest ? <p className="text-sm" data-testid="m-latest-feedback"><strong>{m.feedback.latest.most_useful || m.feedback.latest.category}</strong><br />{m.feedback.latest.improve || m.feedback.latest.build_next || m.feedback.latest.resources_requested}</p> : <p className="text-sm text-ink-soft">None yet.</p>}
        </Box>
        <Box title="Latest impact review">
          {m?.impact?.latest ? <p className="text-sm" data-testid="m-latest-impact">Actions: <strong>{m.impact.latest.actions_implemented}</strong> · Improvements: <strong>{m.impact.latest.improvements_observed}</strong><br />{m.impact.latest.impact_summary}</p> : <p className="text-sm text-ink-soft">None yet.</p>}
        </Box>
        <Box title="Most requested features"><CountList title="" rows={(m?.top_features || []).slice(0, 6).map((f) => ({ name: `${f.title} (${f.status})`, count: f.demand }))} /></Box>
      </div>
    </div>
  );
};

export const DownloadsTab = ({ m, v }) => {
  const d = m?.downloads || {};
  return (
    <div className="space-y-5" data-testid="admin-downloads-tab">
      <Box title="Download trend (30 days)"><Bars data={d.daily || []} label="Daily downloads" /></Box>
      <div className="grid gap-5 md:grid-cols-3">
        <Box title="Resources"><CountList title="" rows={d.resources || []} /></Box>
        <Box title="Referrers"><CountList title="" rows={d.referrers || []} /></Box>
        <Box title="Device types"><CountList title="" rows={d.devices || []} /></Box>
      </div>
      <h2 className="pt-4 font-display text-3xl font-medium">Visitors</h2>
      <VisitorCards v={v} />
      <Box title="Page views, last 30 days"><Bars data={v?.daily || []} label="Daily page views" /></Box>
      <div className="grid gap-5 md:grid-cols-2">
        <Box title="Most visited pages"><CountList title="" rows={v?.pages || []} /></Box>
        <Box title="Visitor devices"><CountList title="" rows={v?.devices || []} /></Box>
      </div>
      <p className="text-sm text-ink-soft">Anonymous: resource, time, referring site, device type and a salted hash of the network address only.</p>
    </div>
  );
};

export const ImpactTab = ({ m, items }) => {
  const i = m?.impact || {};
  return (
    <div className="space-y-5" data-testid="admin-impact-tab">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Card label="Reviews received" value={i.total} testid="i-total" />
        <Card label="Actions implemented" value={i.actions_implemented} testid="i-actions" />
        <Card label="Improvements reported" value={i.improvements_observed} testid="i-improve" />
        <Card label="Recommendation rate" value={`${i.recommend_rate ?? 0}%`} testid="i-rate" />
      </div>
      <div className="grid gap-5 md:grid-cols-3">
        <Box title="Improvement areas"><CountList title="" rows={i.areas || []} /></Box>
        <Box title="Common challenges"><ul className="space-y-2 text-sm">{(i.challenges || []).map((c, k) => <li key={k}>• {c}</li>)}{!i.challenges?.length && <li className="text-ink-soft">None yet.</li>}</ul></Box>
        <Box title="Potential case studies"><ul className="space-y-2 text-sm" data-testid="i-case-studies">{(i.case_studies || []).map((c, k) => <li key={k}><strong>{c.school}</strong>{c.email ? ` · ${c.email}` : ""}<br />{c.summary}</li>)}{!i.case_studies?.length && <li className="text-ink-soft">None yet.</li>}</ul></Box>
      </div>
      <Box title="All reviews">
        <ul className="space-y-3 text-sm">{items.map((r) => (
          <li key={r.id} className="border-b border-line pb-3" data-testid={`impact-item-${r.id}`}>
            <p className="font-bold">{new Date(r.created_at).toLocaleDateString("en-GB")} · {r.school || "Anonymous"} · Actions {r.actions_implemented} · Improvements {r.improvements_observed} · Recommend {r.recommend}</p>
            {r.impact_summary && <p className="mt-1">{r.impact_summary}</p>}
            {r.remaining_challenges && <p className="mt-1 text-ink-soft">Challenges: {r.remaining_challenges}</p>}
            {r.support_requested && <p className="mt-1 text-ink-soft">Support requested: {r.support_requested}</p>}
          </li>
        ))}{!items.length && <li className="text-ink-soft">No reviews yet.</li>}</ul>
      </Box>
    </div>
  );
};
